/**
 * Small Web Audio helpers for building short, percussive UI sounds.
 *
 * Everything here is synthesised rather than loaded, so the game ships with a
 * complete sound design and zero audio assets. The trade is obvious: struck
 * wood and rustled parchment are approximations, not recordings. See
 * `sounds.ts` for how to swap any one of them for a real file.
 *
 * The recurring trick is to avoid pure tones, which read as "beep" and would
 * fight the grounded look. Almost every sound here is filtered noise with a
 * fast decay, sometimes with a quiet pitched body underneath for weight.
 */

export interface Voice {
  ctx: AudioContext;
  out: AudioNode;
  /** Seconds, relative to ctx.currentTime, that the sound should start. */
  at: number;
}

/** One second of white noise, generated once and reused by every voice. */
let noiseBuffer: AudioBuffer | null = null;

export function getNoise(ctx: AudioContext): AudioBuffer {
  if (noiseBuffer && noiseBuffer.sampleRate === ctx.sampleRate) return noiseBuffer;
  const length = Math.floor(ctx.sampleRate);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  noiseBuffer = buffer;
  return buffer;
}

export interface NoiseOptions {
  duration: number;
  /** Peak gain before the decay. */
  gain?: number;
  type?: BiquadFilterType;
  frequency?: number;
  /** Filter resonance. Higher values ring more, which reads as metal. */
  q?: number;
  /** Sweep the filter to this frequency across the sound. */
  sweepTo?: number;
  /** Shape of the fall-off. Higher decays faster. */
  curve?: number;
}

/** A filtered burst of noise: knocks, rustles, scrapes, impacts. */
export function noiseBurst(voice: Voice, options: NoiseOptions): void {
  const { ctx, out, at } = voice;
  const {
    duration,
    gain = 0.5,
    type = 'bandpass',
    frequency = 1200,
    q = 1,
    sweepTo,
    curve = 1,
  } = options;

  const source = ctx.createBufferSource();
  source.buffer = getNoise(ctx);
  // Start at a random offset so repeated plays are not identical.
  const offset = Math.random() * 0.5;

  const filter = ctx.createBiquadFilter();
  filter.type = type;
  filter.frequency.setValueAtTime(frequency, at);
  filter.Q.value = q;
  if (sweepTo !== undefined) {
    filter.frequency.exponentialRampToValueAtTime(Math.max(20, sweepTo), at + duration);
  }

  const env = ctx.createGain();
  env.gain.setValueAtTime(0, at);
  env.gain.linearRampToValueAtTime(gain, at + 0.004);
  // setTargetAtTime gives a natural exponential tail rather than a linear cut.
  env.gain.setTargetAtTime(0, at + 0.006, duration / (3 * curve));

  source.connect(filter).connect(env).connect(out);
  source.start(at, offset, duration + 0.05);
  source.stop(at + duration + 0.05);
}

export interface ToneOptions {
  frequency: number;
  duration: number;
  gain?: number;
  type?: OscillatorType;
  /** Glide to this frequency over the sound. */
  slideTo?: number;
  /** Delay before the tone starts, in seconds. */
  delay?: number;
}

/** A pitched body. Used sparingly, under noise, to give an impact weight. */
export function tone(voice: Voice, options: ToneOptions): void {
  const { ctx, out, at } = voice;
  const { frequency, duration, gain = 0.2, type = 'sine', slideTo, delay = 0 } = options;
  const start = at + delay;

  const osc = ctx.createOscillator();
  osc.type = type;
  osc.frequency.setValueAtTime(frequency, start);
  if (slideTo !== undefined) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, slideTo), start + duration);
  }

  const env = ctx.createGain();
  env.gain.setValueAtTime(0, start);
  env.gain.linearRampToValueAtTime(gain, start + 0.008);
  env.gain.setTargetAtTime(0, start + 0.012, duration / 3);

  osc.connect(env).connect(out);
  osc.start(start);
  osc.stop(start + duration + 0.1);
}

/** Randomise a value by a fraction, so repeats never sound mechanical. */
export function vary(value: number, amount = 0.06): number {
  return value * (1 + (Math.random() * 2 - 1) * amount);
}
